import { Box } from "@chakra-ui/react";
import { useColorMode } from "@providers/colorModeProvider";
import React, { useEffect, useMemo } from "react";
import {
  BarChart,
  CartesianGrid,
  CartesianGridProps,
  Cell,
  LabelList,
  Bar as RechartsBar,
  ResponsiveContainer,
} from "recharts";
import { ChartXAxis, ChartYAxis } from "./ChartAxis";
import { ChartLegend } from "./ChartLegend";
import { ChartTooltip } from "./ChartTooltip";
import { colorScheme } from "./colorSchemes";
import {
  BarSeries,
  BarSeriesLabelProps,
  ColorScheme,
  ReusableBarChartProps,
} from "./interface";
import { resolveColorToken, useFontSizeInPx, useResolvedColor } from "./utils";
import { generateRandomColors } from "./utils/generateRandomColors";

const BarWithLabel: React.FC<{
  barSeries: BarSeries;
  index: number;
  showActiveBar: boolean;
  effectiveColorScheme: ColorScheme;
  data: any[];
}> = ({ barSeries, index, showActiveBar, effectiveColorScheme, data }) => {
  const defaultLabel = effectiveColorScheme.series.label;
  const defaultLabelFill = useResolvedColor(defaultLabel.fill);
  const defaultLabelFontSize = useFontSizeInPx(defaultLabel.fontSize);
  const defaultLabelFontWeight = defaultLabel.fontWeight;

  const labelConfig = useMemo(() => {
    if (barSeries.label === false) return null;
    if (typeof barSeries.label === "function") {
      return { content: barSeries.label };
    }
    if (barSeries.label === true || barSeries.label === undefined) {
      return {
        enabled: true,
        position: defaultLabel.position || "top",
        fill: defaultLabelFill,
        fontSize: defaultLabelFontSize,
        fontWeight: defaultLabelFontWeight,
      };
    }
    return barSeries.label as BarSeriesLabelProps;
  }, [
    barSeries.label,
    defaultLabel,
    defaultLabelFill,
    defaultLabelFontSize,
    defaultLabelFontWeight,
  ]);

  const customFontSizeToken =
    labelConfig &&
    typeof labelConfig === "object" &&
    !("content" in labelConfig) &&
    "fontSize" in labelConfig
      ? (labelConfig as BarSeriesLabelProps).fontSize
      : undefined;
  const customFontSizePx =
    customFontSizeToken && typeof customFontSizeToken !== "number"
      ? useFontSizeInPx(customFontSizeToken)
      : customFontSizeToken;

  const finalFontSize =
    labelConfig &&
    typeof labelConfig === "object" &&
    !("content" in labelConfig) &&
    customFontSizePx
      ? customFontSizePx
      : defaultLabelFontSize;

  const finalFontWeight =
    labelConfig &&
    typeof labelConfig === "object" &&
    !("content" in labelConfig)
      ? (labelConfig as BarSeriesLabelProps).fontWeight ??
        defaultLabelFontWeight
      : defaultLabelFontWeight;

  useEffect(() => {
    if (!labelConfig || labelConfig.enabled === false) return;

    const styleId = `recharts-label-styles-${barSeries.dataKey}-${index}`;
    const existing = document.getElementById(styleId);
    if (existing) return;

    const parts: string[] = [];
    if (finalFontSize) parts.push(`font-size: ${finalFontSize}px !important;`);
    if (finalFontWeight)
      parts.push(`font-weight: ${finalFontWeight} !important;`);

    if (parts.length === 0) return;

    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `.recharts-layer.recharts-label-list .recharts-label { ${parts.join(
      " "
    )} }`;
    document.head.appendChild(style);

    return () => {
      const styleEl = document.getElementById(styleId);
      if (styleEl) document.head.removeChild(styleEl);
    };
  }, [finalFontSize, finalFontWeight, barSeries.dataKey, index, labelConfig]);

  // Get activeBar config and resolve colors
  const activeBarConfig = effectiveColorScheme.series.activeBar;
  const activeBarOverlayFill = activeBarConfig?.overlayFill
    ? useResolvedColor(activeBarConfig.overlayFill)
    : undefined;
  const activeBarStroke = activeBarConfig?.stroke
    ? useResolvedColor(activeBarConfig.stroke)
    : undefined;

  const activeBarProps = useMemo(() => {
    if (!showActiveBar || barSeries.activeBar === false) {
      return false;
    }

    // If activeBar is true or undefined, use default activeBar config
    if (barSeries.activeBar === true || barSeries.activeBar === undefined) {
      const hasOverlayFill = activeBarConfig?.overlayFill;

      // If overlayFill exists, render custom component with overlay
      if (hasOverlayFill) {
        return (props: any) => {
          const { x, y, width, height, fill } = props;
          return (
            <g>
              {/* Original bar (preserved) */}
              <rect
                x={x}
                y={y}
                width={width}
                height={height}
                fill={fill}
                rx={props.radius?.[0] || 0}
                ry={props.radius?.[1] || 0}
              />
              {/* Overlay with semi-transparent fill */}
              <rect
                x={x}
                y={y}
                width={width}
                height={height}
                fill={activeBarOverlayFill || "none"}
                stroke={activeBarStroke || "none"}
                strokeWidth={activeBarConfig.strokeWidth || 2}
                rx={props.radius?.[0] || 0}
                ry={props.radius?.[1] || 0}
              />
            </g>
          );
        };
      }

      // Default behavior: use fill if provided, otherwise just stroke
      return {
        stroke: activeBarStroke || "none",
        strokeWidth: activeBarConfig?.strokeWidth || 2,
      };
    }

    // If activeBar is a function (custom activeBar component), wrap it to inject defaults
    if (typeof barSeries.activeBar === "function") {
      const activeBarFn = barSeries.activeBar;
      return (props: any) => {
        const result = (activeBarFn as (props: any) => React.ReactElement)(
          props
        );
        // If result is a React element (like a rect), clone it and add default styles if missing
        if (React.isValidElement(result)) {
          const element = result as React.ReactElement<any>;
          const updates: any = {};

          // For activeBar, preserve original fill from props - don't inject defaultBarFill
          // Only inject stroke and strokeWidth if not set
          if (!element.props?.stroke && activeBarStroke) {
            updates.stroke = activeBarStroke;
          }

          if (!element.props?.strokeWidth && activeBarConfig?.strokeWidth) {
            updates.strokeWidth = activeBarConfig.strokeWidth;
          }

          // If overlay is configured and element is a rect, wrap it in a group with overlay
          if (activeBarOverlayFill && element.type === "rect") {
            return (
              <g>
                {React.cloneElement(element, updates)}
                <rect
                  x={element.props.x}
                  y={element.props.y}
                  width={element.props.width}
                  height={element.props.height}
                  fill={activeBarOverlayFill}
                  stroke={activeBarStroke || "none"}
                  strokeWidth={activeBarConfig?.strokeWidth || 2}
                  rx={element.props.rx || 0}
                  ry={element.props.ry || 0}
                />
              </g>
            );
          }

          // If there are updates, apply them
          if (Object.keys(updates).length > 0) {
            return React.cloneElement(element, updates);
          }
        }
        return result;
      };
    }

    // If activeBar is an object, merge with defaults if properties are missing
    // Note: Don't inject fill for activeBar - it should preserve the original bar's fill
    if (
      typeof barSeries.activeBar === "object" &&
      barSeries.activeBar !== null &&
      !React.isValidElement(barSeries.activeBar)
    ) {
      const activeBarObj = barSeries.activeBar as Record<string, any>;
      return {
        ...(activeBarStroke &&
          !activeBarObj.stroke && { stroke: activeBarStroke }),
        ...(activeBarConfig?.strokeWidth &&
          !activeBarObj.strokeWidth && {
            strokeWidth: activeBarConfig.strokeWidth,
          }),
        ...activeBarObj,
      };
    }

    return barSeries.activeBar;
  }, [
    showActiveBar,
    barSeries.activeBar,
    activeBarConfig,
    activeBarOverlayFill,
    activeBarStroke,
  ]);

  const customFillColor =
    labelConfig &&
    typeof labelConfig === "object" &&
    !("content" in labelConfig) &&
    (labelConfig as BarSeriesLabelProps).fill
      ? (labelConfig as BarSeriesLabelProps).fill
      : undefined;
  const customFill = customFillColor
    ? useResolvedColor(customFillColor)
    : undefined;
  const resolvedFill = customFill ?? defaultLabelFill;

  const labelProps = useMemo(() => {
    if (!labelConfig || labelConfig.enabled === false) return null;

    // If label is a raw function, wrap it to ensure full row payload
    if (typeof barSeries.label === "function") {
      const original = barSeries.label;
      const wrapped = (lp: any) => {
        const i = typeof lp?.index === "number" ? lp.index : index;
        const row = lp?.payload ?? (Array.isArray(data) ? data[i] : undefined);
        return original({ ...lp, payload: row });
      };
      return { content: wrapped } as any;
    }

    // If content exists, pass payload row and defaultStyles so consumers can spread onto <text>
    if ("content" in labelConfig && labelConfig.content) {
      const original = labelConfig.content;
      const defaultStyles = {
        fill: resolvedFill as string,
        fontSize: Number(finalFontSize),
        fontWeight: defaultLabelFontWeight as any,
      } as const;
      const defaultStylesAttr = defaultStyles; // attribute-friendly
      const wrappedContent = (lp: any) => {
        const i = typeof lp?.index === "number" ? lp.index : index;
        const row = lp?.payload ?? (Array.isArray(data) ? data[i] : undefined);
        const res = original({
          ...lp,
          payload: row,
          defaultStyles,
          defaultStylesAttr,
        });
        // If user returns JSX, clone and inject missing font attrs like the default path
        if (React.isValidElement(res)) {
          const currStyle = (((res as any).props && (res as any).props.style) ||
            {}) as any;
          const nextStyle = {
            ...currStyle,
            fontSize:
              (res as any).props?.style?.fontSize !== undefined
                ? (res as any).props.style.fontSize
                : defaultStyles.fontSize,
            fontWeight:
              (res as any).props?.style?.fontWeight !== undefined
                ? (res as any).props.style.fontWeight
                : defaultStyles.fontWeight,
            fill:
              (res as any).props?.style?.fill !== undefined
                ? (res as any).props.style.fill
                : defaultStyles.fill,
          } as any;
          return React.cloneElement(res as any, {
            // Preserve positioning props from lp (critical for SVG text positioning)
            // User's custom x/y props take precedence if they exist (for custom positioning)
            x:
              (res as any).props?.x !== undefined
                ? (res as any).props.x
                : lp?.x !== undefined
                ? lp.x
                : undefined,
            y:
              (res as any).props?.y !== undefined
                ? (res as any).props.y
                : lp?.y !== undefined
                ? lp.y
                : undefined,
            // Preserve other positioning props (user's props take precedence)
            ...(lp?.offset !== undefined &&
            (res as any).props?.offset === undefined
              ? { offset: lp.offset }
              : {}),
            ...(lp?.position !== undefined &&
            (res as any).props?.position === undefined
              ? { position: lp.position }
              : {}),
            ...(lp?.viewBox !== undefined &&
            (res as any).props?.viewBox === undefined
              ? { viewBox: lp.viewBox }
              : {}),
            // Preserve font-related props
            fontSize:
              (res as any).props?.fontSize !== undefined
                ? (res as any).props.fontSize
                : defaultStyles.fontSize,
            fontWeight:
              (res as any).props?.fontWeight !== undefined
                ? (res as any).props.fontWeight
                : defaultStyles.fontWeight,
            fill:
              (res as any).props?.fill !== undefined
                ? (res as any).props.fill
                : defaultStyles.fill,
            style: nextStyle,
          });
        }
        return res;
      };

      return {
        dataKey: barSeries.dataKey,
        position: (labelConfig as any).position ?? defaultLabel.position,
        fill: resolvedFill,
        fontSize: finalFontSize,
        ...(labelConfig.fontWeight !== undefined && {
          fontWeight: labelConfig.fontWeight,
        }),
        content: wrappedContent,
        ...(labelConfig.offset !== undefined && { offset: labelConfig.offset }),
        ...(labelConfig.angle !== undefined && { angle: labelConfig.angle }),
        ...Object.fromEntries(
          Object.entries(labelConfig).filter(
            ([k]) =>
              ![
                "position",
                "fill",
                "fontSize",
                "fontWeight",
                "content",
                "offset",
                "angle",
                "enabled",
              ].includes(k)
          )
        ),
      } as any;
    }

    const config = labelConfig as BarSeriesLabelProps;
    const {
      enabled: _enabled,
      fill: _fill,
      fontSize: _fontSize,
      fontWeight: _fontWeight,
      ...restProps
    } = config;

    const formatter = config.formatter ? config.formatter : undefined;

    return {
      dataKey: barSeries.dataKey,
      position: config.position ?? defaultLabel.position,
      fill: resolvedFill,
      fontSize: finalFontSize,
      ...(config.fontWeight !== undefined && { fontWeight: config.fontWeight }),
      ...(formatter !== undefined && { formatter }),
      ...(config.offset !== undefined && { offset: config.offset }),
      ...(config.angle !== undefined && { angle: config.angle }),
      ...restProps,
    };
  }, [
    labelConfig,
    barSeries.dataKey,
    resolvedFill,
    finalFontSize,
    data,
    index,
    defaultLabel.position,
  ]);

  return (
    <RechartsBar
      dataKey={barSeries.dataKey}
      name={barSeries.name}
      fill={barSeries.fill || effectiveColorScheme.series.defaultBar?.fill}
      radius={barSeries.radius || [4, 4, 0, 0]}
      activeBar={activeBarProps}
      stackId={barSeries.stackId}
    >
      {barSeries.cells &&
        barSeries.cells.map((cell, cellIndex) => (
          <Cell
            key={cell.key || `cell-${index}-${cellIndex}`}
            fill={cell.fill}
          />
        ))}
      {labelProps && labelProps.enabled !== false && (
        <LabelList dataKey={barSeries.dataKey} {...labelProps} />
      )}
    </RechartsBar>
  );
};

export const Bar: React.FC<ReusableBarChartProps> = ({
  data,
  generateColors = false,
  series,
  height = "100%",
  width = "100%",
  margin = { top: 30, right: 20, bottom: 30, left: 20 },
  xAxis,
  yAxis,
  grid,
  tooltip,
  legend,
  showActiveBar = true,
  className,
  colorMode: colorModeProp,
  colorScheme: customColorScheme,
  defaultStackId,
  layout = "horizontal",
}) => {
  const { colorMode: contextColorMode } = useColorMode();
  const colorMode = colorModeProp || contextColorMode;

  const generatedColors = useMemo(
    () =>
      generateColors && data?.length
        ? generateRandomColors(data.length, { format: "hex" })
        : undefined,
    [generateColors, data]
  );

  const seriesWithColors = useMemo(() => {
    const withStackDefaults = series.map((s) => ({
      ...s,
      stackId: s.stackId ?? defaultStackId,
    }));

    if (!generatedColors || !data?.length) return withStackDefaults;

    return withStackDefaults.map((barSeries, seriesIndex) => {
      if (barSeries.cells?.length) return barSeries;

      return {
        ...barSeries,
        cells: data.map((entry: any, index: number) => ({
          fill: generatedColors![index] || barSeries.fill || "gray",
          key: `cell-${seriesIndex}-${index}-${
            entry.id || entry[legend?.labelKey || "id"] || index
          }`,
        })),
      };
    });
  }, [series, generatedColors, data, legend?.labelKey, defaultStackId]);

  const effectiveColorScheme = useMemo(() => {
    const base = customColorScheme
      ? Object.entries(customColorScheme).reduce(
          (acc, [key, value]) => {
            const baseValue = colorScheme[colorMode][key as keyof ColorScheme];
            acc[key] =
              value &&
              typeof value === "object" &&
              baseValue &&
              typeof baseValue === "object"
                ? { ...baseValue, ...value }
                : value ?? baseValue;
            return acc;
          },
          { ...colorScheme[colorMode] } as any
        )
      : colorScheme[colorMode];

    return {
      ...base,
      xAxis: {
        ...base.xAxis,
        tick: {
          ...base.xAxis.tick,
          fill: resolveColorToken(base.xAxis.tick.fill),
        },
        axisLine: {
          ...base.xAxis.axisLine,
          stroke: resolveColorToken(base.xAxis.axisLine.stroke),
        },
      },
      yAxis: {
        ...base.yAxis,
        tick: {
          ...base.yAxis.tick,
          fill: resolveColorToken(base.yAxis.tick.fill),
        },
        axisLine: {
          ...base.yAxis.axisLine,
          stroke: resolveColorToken(base.yAxis.axisLine.stroke),
        },
      },
      grid: { ...base.grid, stroke: resolveColorToken(base.grid.stroke) },
      tooltip: {
        ...base.tooltip,
        color: resolveColorToken(base.tooltip.color),
        bg: resolveColorToken(base.tooltip.bg),
        border: resolveColorToken(base.tooltip.border),
        titleColor: resolveColorToken(base.tooltip.titleColor),
        valueColor: resolveColorToken(base.tooltip.valueColor),
      },
      legend: {
        ...base.legend,
        ...(base.legend.fill && { fill: resolveColorToken(base.legend.fill) }),
      },
      series: {
        ...base.series,
        defaultBar: base.series.defaultBar
          ? {
              ...base.series.defaultBar,
              fill: base.series.defaultBar.fill
                ? resolveColorToken(base.series.defaultBar.fill)
                : undefined,
              stroke: base.series.defaultBar.stroke
                ? resolveColorToken(base.series.defaultBar.stroke)
                : undefined,
            }
          : undefined,
        activeBar: base.series.activeBar
          ? {
              ...base.series.activeBar,
              fill: base.series.activeBar.fill
                ? resolveColorToken(base.series.activeBar.fill)
                : undefined,
              overlayFill: base.series.activeBar.overlayFill
                ? resolveColorToken(base.series.activeBar.overlayFill)
                : undefined,
              stroke: base.series.activeBar.stroke
                ? resolveColorToken(base.series.activeBar.stroke)
                : undefined,
            }
          : undefined,
        label: {
          ...base.series.label,
          fill: resolveColorToken(base.series.label.fill),
        },
        ...(base.series.legend && {
          legend: {
            ...base.series.legend,
            ...(base.series.legend.fill && {
              fill: resolveColorToken(base.series.legend.fill),
            }),
          },
        }),
      },
    };
  }, [colorMode, customColorScheme]);

  const defaultGridProps: CartesianGridProps = {
    strokeDasharray: "3 3",
    stroke: resolveColorToken(effectiveColorScheme.grid.stroke),
    ...grid,
  };

  const chartStyleId = useMemo(
    () =>
      `chart-${effectiveColorScheme.xAxis.tick.fontSize}-${
        effectiveColorScheme.yAxis.tick.fontSize
      }-${Math.random().toString(36).substring(2, 11)}`,
    [
      effectiveColorScheme.xAxis.tick.fontSize,
      effectiveColorScheme.yAxis.tick.fontSize,
    ]
  );

  return (
    <Box className={className} w={width} h={height} position="relative">
      <ResponsiveContainer width={width} height={height}>
        <BarChart data={data} margin={margin} layout={layout}>
          <CartesianGrid {...defaultGridProps} />
          <ChartXAxis
            {...xAxis}
            colorScheme={effectiveColorScheme}
            styleId={chartStyleId}
            type={layout === "vertical" ? "number" : "category"}
          />
          <ChartYAxis
            {...yAxis}
            colorScheme={effectiveColorScheme}
            styleId={chartStyleId}
            type={layout === "vertical" ? "category" : "number"}
          />
          <ChartLegend
            legend={
              legend
                ? {
                    ...legend,
                    colors: generatedColors,
                    generateColors: generateColors && !!legend.labelKey,
                  }
                : undefined
            }
            data={data}
            series={seriesWithColors}
            colorScheme={effectiveColorScheme}
          />
          <ChartTooltip
            cursor={false}
            tooltip={tooltip}
            colorScheme={effectiveColorScheme}
          />
          {seriesWithColors.map((barSeries, index) => (
            <BarWithLabel
              key={`bar-${barSeries.dataKey}-${index}`}
              barSeries={barSeries}
              index={index}
              showActiveBar={showActiveBar}
              effectiveColorScheme={effectiveColorScheme}
              data={data as any[]}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};
